import React, { useState, useEffect, useMemo } from 'react';

export default function PropietarioComunicados({ comunicados = [] }) {
  const [busqueda, setBusqueda] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState('');
  const [comunicadoSeleccionado, setComunicadoSeleccionado] = useState(null);

  // Función para formatear fechas con ceros adelante (ej. 04/09/2026)
  const formatearFecha = (fechaInput) => {
    if (!fechaInput) return 'Reciente';
    
    const fechaObj = new Date(fechaInput);
    if (!isNaN(fechaObj.getTime())) {
      const dia = String(fechaObj.getDate()).padStart(2, '0');
      const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
      const anio = fechaObj.getFullYear();
      return `${dia}/${mes}/${anio}`;
    }
    
    const partes = String(fechaInput).split(/[-/]/);
    if (partes.length === 3) {
      if (partes[0].length === 4) {
        const [anio, mes, dia] = partes;
        return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`;
      } else {
        const [dia, mes, anio] = partes;
        return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`;
      }
    }

    return fechaInput;
  };

  const comunicadosFiltrados = useMemo(() => {
    return comunicados.filter((com) => {
      const textoBuscado = busqueda.toLowerCase().trim();
      const titulo = (com.titulo || '').toLowerCase();
      const contenido = (com.mensaje || com.descripcion || com.contenido || '').toLowerCase();
      const coincideTexto = !textoBuscado || titulo.includes(textoBuscado) || contenido.includes(textoBuscado);

      const fechaOriginal = com.fecha || com.fechaSubida || '';
      const fechaFormateada = formatearFecha(fechaOriginal).toLowerCase();
      const coincideFecha = !fechaFiltro || fechaFormateada.includes(fechaFiltro.toLowerCase().trim()) || fechaOriginal.toLowerCase().includes(fechaFiltro.toLowerCase().trim());

      return coincideTexto && coincideFecha;
    });
  }, [comunicados, busqueda, fechaFiltro]);

  useEffect(() => {
    if (comunicadosFiltrados.length > 0) {
      const existe = comunicadoSeleccionado && comunicadosFiltrados.some(
        c => (c.id || c.fecha) === (comunicadoSeleccionado.id || comunicadoSeleccionado.fecha)
      );
      if (!existe) {
        setComunicadoSeleccionado(comunicadosFiltrados[0]);
      }
    } else {
      setComunicadoSeleccionado(null);
    }
  }, [comunicadosFiltrados, comunicadoSeleccionado]);

  const esImagen = (url) => {
    if (!url) return false;
    return url.match(/\.(jpeg|jpg|gif|png|webp)(\?[^/]*)?$/i) || url.includes('image');
  };

  return (
    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)', boxSizing: 'border-box', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      
      {/* Encabezado Principal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{ backgroundColor: '#e0f2fe', color: '#0284c7', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', boxShadow: '0 4px 6px -1px rgba(2, 132, 199, 0.2)', flexShrink: 0 }}>
          📬
        </div>
        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem', fontWeight: '800', wordBreak: 'break-word' }}>
          Bandeja de Comunicados Oficiales
        </h3>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.5' }}>
        Consulta las circulares, avisos urgentes y notificaciones importantes emitidas por la administración de manera ordenada.
      </p>

      {/* Barra de Filtros (Búsqueda por Palabra Clave + Filtro por Fecha) */}
      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px', marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', alignItems: 'center', boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.02)', boxSizing: 'border-box' }}>
        
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: '700', display: 'block', marginBottom: '6px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            🔍 Palabra Clave o Asunto:
          </label>
          <input 
            type="text"
            placeholder="Ej. Mantenimiento, Cuota, Asamblea..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '12px 14px', 
              borderRadius: '10px', 
              border: '1px solid #cbd5e1', 
              fontSize: '0.9rem', 
              boxSizing: 'border-box',
              backgroundColor: 'white',
              outline: 'none',
              color: '#1e293b',
              fontWeight: '500'
            }}
          />
        </div>

        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: '700', display: 'block', marginBottom: '6px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            📅 Filtrar por Fecha:
          </label>
          <input 
            type="text"
            placeholder="Ej. 04/09/2026 o 2026..."
            value={fechaFiltro}
            onChange={(e) => setFechaFiltro(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '12px 14px', 
              borderRadius: '10px', 
              border: '1px solid #cbd5e1', 
              fontSize: '0.9rem', 
              boxSizing: 'border-box',
              backgroundColor: 'white',
              outline: 'none',
              color: '#1e293b',
              fontWeight: '500'
            }}
          />
        </div>

      </div>

      {comunicados.length === 0 ? (
        <div style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '30px 15px', textAlign: 'center', color: '#64748b', boxSizing: 'border-box' }}>
          <span style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}>📭</span>
          <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.9rem' }}>No hay comunicados o avisos recientes en tu casilla en este momento.</p>
        </div>
      ) : comunicadosFiltrados.length === 0 ? (
        <div style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '25px 15px', textAlign: 'center', color: '#64748b', boxSizing: 'border-box' }}>
          <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.88rem' }}>No se encontraron comunicados que coincidan con los filtros aplicados.</p>
        </div>
      ) : (
        /* Estructura Adaptativa: Columna en celulares, Dos Columnas en computadoras */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', alignItems: 'start', boxSizing: 'border-box' }}>
          
          {/* COLUMNA IZQUIERDA: LISTA DE ASUNTOS CON SCROLL FIJO */}
          <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ padding: '12px 14px', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '0.8rem', fontWeight: 'bold', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Asuntos ({comunicadosFiltrados.length})
            </div>
            
            {/* Contenedor con altura máxima controlada y scroll vertical */}
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '420px', overflowY: 'auto', scrollbarWidth: 'thin' }}>
              {comunicadosFiltrados.map((com, index) => {
                const idUnico = com.id || com.fecha || index;
                const estaSeleccionado = comunicadoSeleccionado && (comunicadoSeleccionado.id || comunicadoSeleccionado.fecha || index) === idUnico;
                const fechaLimpia = formatearFecha(com.fecha || com.fechaSubida);
                
                return (
                  <div 
                    key={idUnico}
                    onClick={() => setComunicadoSeleccionado(com)}
                    style={{ 
                      padding: '12px 14px',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor: estaSeleccionado ? '#e0f2fe' : 'white',
                      borderLeft: estaSeleccionado ? '4px solid #0284c7' : '4px solid transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.75rem', color: estaSeleccionado ? '#0369a1' : '#64748b', fontWeight: '600' }}>
                        📅 {fechaLimpia}
                      </span>
                    </div>
                    <h5 style={{ margin: 0, color: estaSeleccionado ? '#0f172a' : '#334155', fontSize: '0.88rem', fontWeight: estaSeleccionado ? '700' : '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {com.titulo || 'Comunicado Importante'}
                    </h5>
                  </div>
                );
              })}
            </div>
          </div>

          {/* COLUMNA DERECHA: PANEL DE LECTURA DEL COMUNICADO */}
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', minHeight: '420px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.01)', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            {comunicadoSeleccionado ? (
              <div style={{ width: '100%', boxSizing: 'border-box' }}>
                {/* Cabecera del mensaje */}
                <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', marginBottom: '16px', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, color: '#0f172a', fontSize: '1.15rem', fontWeight: '800', wordBreak: 'break-word', flex: 1, minWidth: '180px' }}>
                      {comunicadoSeleccionado.titulo || 'Comunicado Importante'}
                    </h4>
                    <span style={{ fontSize: '0.78rem', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '4px 10px', borderRadius: '20px', fontWeight: '650', whiteSpace: 'nowrap' }}>
                      📅 {formatearFecha(comunicadoSeleccionado.fecha || comunicadoSeleccionado.fechaSubida)}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', wordBreak: 'break-word' }}>
                    De: <strong>Administración MMD&L Edificios SAC</strong>
                  </p>
                </div>

                {/* Contenido / Cuerpo del mensaje completo */}
                <div style={{ color: '#334155', lineHeight: '1.6', fontSize: '0.9rem', whiteSpace: 'pre-wrap', marginBottom: '20px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {comunicadoSeleccionado.mensaje || comunicadoSeleccionado.descripcion || comunicadoSeleccionado.contenido}
                </div>

                {/* Archivo Adjunto */}
                {comunicadoSeleccionado.archivoUrl && (
                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #f1f5f9', boxSizing: 'border-box' }}>
                    {esImagen(comunicadoSeleccionado.archivoUrl) ? (
                      <div>
                        <p style={{ fontSize: '0.82rem', fontWeight: '700', color: '#475569', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>🖼️</span> Imagen adjunta:
                        </p>
                        <a href={comunicadoSeleccionado.archivoUrl} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={comunicadoSeleccionado.archivoUrl} 
                            alt="Adjunto del comunicado" 
                            style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '10px', border: '1px solid #cbd5e1', objectFit: 'contain', display: 'block', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} 
                          />
                        </a>
                      </div>
                    ) : (
                      <a 
                        href={comunicadoSeleccionado.archivoUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          backgroundColor: '#f0fdf4',
                          color: '#15803d',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          fontWeight: 'bold', 
                          textDecoration: 'none',
                          fontSize: '0.85rem',
                          border: '1px solid #bbf7d0',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          wordBreak: 'break-word'
                        }}
                      >
                        <span>📎</span> Ver documento PDF adjunto <span>↗</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', textAlign: 'center', padding: '30px 15px' }}>
                <span style={{ fontSize: '2.2rem', marginBottom: '10px' }}>👈</span>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem' }}>Selecciona un comunicado de la izquierda para ver su contenido completo.</p>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}